import { Dialog, DialogContent, DialogActions, Snackbar } from "@mui/material";
import { useState, useEffect, useRef } from "react";
import SelectAutoComplete from "../../ui/selectAutoComplete/SelectAutoComplete";
import CustomButton from "../../ui/button/Button";
import {
  getTiposNotificaciones,
  enviarNotificacion,
  getOldNotifications,
} from "../../components/functions/Notifications";
import { fetchInstallers } from "../../components/functions/Functions";
import AllNotificationsModal from "./AllNotificationsModal";

const NotificationsModal = ({ API_URL, open, onClose, companies }) => {
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [tiposNotificaciones, setTiposNotificaciones] = useState([]);
  const [instaladores, setInstaladores] = useState([]);
  const [empresaSeleccionada, setEmpresaSeleccionada] = useState(null);
  const [imagenNotificacion, setImagenNotificacion] = useState(null);
  // NUEVO: estado para la URL de preview y el modal grande
  const [previewUrl, setPreviewUrl] = useState(null);
  const [openPreview, setOpenPreview] = useState(false);
  const fileInputRef = useRef(null);

  const [notificationsForm, setNotificationsForm] = useState({
    titulo: "",
    mensaje: "",
    rfc: "",
    gafete: "",
    tipoNotificacion: null,
  });
  const [oldNotifications, setOldNotifications] = useState([]);
  const [showAllNotificationsModal, setShowAllNotificationsModal] =
    useState(false);

  useEffect(() => {
    const fetchInstaladores = async () => {
      try {
        const instaladores = await fetchInstallers(
          API_URL,
          empresaSeleccionada.id
        );
        instaladores.forEach((instalador) => {
          instalador.label = `${instalador.numGafete} - ${instalador.nombre} ${instalador.apellidos}`;
        });
        setInstaladores(instaladores);
      } catch (error) {
        console.error("Error al obtener los instaladores:", error);
      }
    };
    if (
      notificationsForm.tipoNotificacion?.name === "INSTALADOR" &&
      empresaSeleccionada
    ) {
      fetchInstaladores();
    }
    if (notificationsForm.tipoNotificacion === null) {
      clearAll();
    }
  }, [notificationsForm.tipoNotificacion, empresaSeleccionada]);

  useEffect(() => {
    const fetchTiposNotificaciones = async () => {
      try {
        const [tipos, notificaciones] = await Promise.all([
          getTiposNotificaciones(API_URL),
          getOldNotifications(API_URL),
        ]);
        setTiposNotificaciones(tipos);
        setOldNotifications(notificaciones);
      } catch (error) {
        console.error("Error al obtener los tipos de notificaciones:", error);
      }
    };
    fetchTiposNotificaciones();
  }, [API_URL]);

  // NUEVO: crea la URL de preview y la limpia cuando cambie/elimine el archivo
  useEffect(() => {
    if (!imagenNotificacion) {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(imagenNotificacion);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [imagenNotificacion]);

  const handleClose = () => {
    clearAll();
    onClose();
  };

  const clearAll = () => {
    setNotificationsForm({
      titulo: "",
      mensaje: "",
      rfc: "",
      gafete: "",
      tipoNotificacion: null,
    });
    setEmpresaSeleccionada(null);
    setInstaladores([]);
    setImagenNotificacion(null);
  };

  const handleSubmit = async () => {
    if (
      notificationsForm.titulo === "" ||
      notificationsForm.mensaje === "" ||
      notificationsForm.tipoNotificacion === null
    ) {
      return;
    }

    if (
      notificationsForm.tipoNotificacion.name === "EMPRESA" &&
      !empresaSeleccionada
    ) {
      return;
    }

    if (
      notificationsForm.tipoNotificacion.name === "INSTALADOR" &&
      (!empresaSeleccionada || !notificationsForm.gafete)
    ) {
      return;
    }

    const file = imagenNotificacion;

    try {
      await enviarNotificacion(API_URL, notificationsForm, file);
      setShowSnackbar(true);
      setSnackbarMessage("Notificación enviada correctamente");
      clearAll();
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarMessage(`Error al enviar la notificación`);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <AllNotificationsModal
        open={showAllNotificationsModal}
        onClose={() => setShowAllNotificationsModal(false)}
        notifications={oldNotifications}
        API_URL={API_URL}
      />
      <Dialog
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 0,
          }}
        >
          {previewUrl && (
            <img
              src={previewUrl}
              alt="Vista previa"
              style={{
                maxWidth: "100%",
                maxHeight: "80vh",
                objectFit: "contain",
                display: "block",
              }}
            />
          )}
        </DialogContent>
      </Dialog>
      <Snackbar
        open={showSnackbar}
        autoHideDuration={3000}
        onClose={() => {
          setShowSnackbar(false);
          setSnackbarMessage("");
        }}
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        slotProps={{
          content: {
            sx: {
              backgroundColor: "white",
              color: "#222",
              justifyContent: "center",
              textAlign: "center",
              fontWeight: "bold",
              fontSize: "16px",
              fontFamily: "Roboto, Arial, sans-serif",
            },
          },
        }}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          padding: "16px 24px",
        }}
      >
        <h2 style={{ margin: 0 }}>Enviar notificación</h2>
        <CustomButton
          color={"#FFFFFF"}
          background={"#223D51"}
          text="Ver notificaciones"
          height="40px"
          fontWeight="700"
          fontSize="14px"
          border="1px solid #D9D9D9"
          type="button"
          width={"140px"}
          onClick={() => setShowAllNotificationsModal(true)}
        />
      </div>
      <DialogContent>
        <div
          style={{
            display: "flex",
            gap: "20px",
            flexDirection: "column",
          }}
        >
          <SelectAutoComplete
            options={tiposNotificaciones}
            placeholder="Tipo de notificación"
            label="Tipo de notificación"
            fieldC="name"
            width={"100%"}
            value={notificationsForm.tipoNotificacion}
            onChange={(e, value) => {
              setNotificationsForm({
                ...notificationsForm,
                tipoNotificacion: value,
              });
            }}
          />
          {["EMPRESA", "INSTALADOR"].includes(
            notificationsForm.tipoNotificacion?.name
          ) && (
            <SelectAutoComplete
              options={companies}
              placeholder="Proveedor"
              label="Proveedor"
              fieldC="razonSocial"
              width={"100%"}
              listHeight="200px"
              value={companies.find(
                (company) => company.rfc === notificationsForm.rfc
              )}
              onChange={(e, value) => {
                setNotificationsForm({
                  ...notificationsForm,
                  rfc: value.rfc,
                });
                setEmpresaSeleccionada(value);
              }}
            />
          )}
          {["INSTALADOR"].includes(notificationsForm.tipoNotificacion?.name) &&
            empresaSeleccionada && (
              <SelectAutoComplete
                options={instaladores}
                placeholder="Instalador"
                label="Instalador"
                listHeight="200px"
                fieldC="label"
                width={"100%"}
                value={instaladores.find(
                  (inst) => inst.numGafete === notificationsForm.gafete
                )}
                onChange={(e, value) => {
                  setNotificationsForm({
                    ...notificationsForm,
                    gafete: value.numGafete,
                  });
                }}
              />
            )}
          <div className="d-flex flex-column" style={{ width: "100%" }}>
            <label
              htmlFor="tituloNotificacion"
              className="form-label form-label-left"
            >
              Título:
            </label>
            <input
              className="form-input"
              type="text"
              id="tituloNotificacion"
              maxLength={30}
              value={notificationsForm.titulo}
              onChange={(e) => {
                setNotificationsForm({
                  ...notificationsForm,
                  titulo: e.target.value,
                });
              }}
              placeholder="Título..."
            />
          </div>
          <div className="d-flex flex-column" style={{ width: "100%" }}>
            <label
              htmlFor="mensajeNotificacion"
              className="form-label form-label-left"
            >
              Mensaje:
            </label>
            <textarea
              maxLength={1024}
              id="mensajeNotificacion"
              value={notificationsForm.mensaje}
              onChange={(e) => {
                setNotificationsForm({
                  ...notificationsForm,
                  mensaje: e.target.value,
                });
              }}
              style={{ width: "100%" }}
              placeholder="Mensaje..."
              rows="4"
            />
          </div>
          <div className="d-flex flex-column" style={{ width: "100%" }}>
            <input
              ref={fileInputRef}
              className="form-input"
              type="file"
              id="imagenNotificacion"
              accept="image/png, image/jpeg, image/gif, image/svg+xml"
              onChange={(e) => {
                const file = e.target.files?.[0] ?? null;
                setImagenNotificacion(file);
              }}
            />
            {previewUrl && (
              <div
                role="button"
                onClick={() => setOpenPreview(true)}
                onKeyDown={(e) =>
                  (e.key === "Enter" || e.key === " ") && setOpenPreview(true)
                }
                tabIndex={0}
                style={{
                  marginTop: 12,
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  cursor: "zoom-in",
                }}
                title="Click para ver en grande"
              >
                <img
                  src={previewUrl}
                  alt="Vista previa"
                  style={{
                    height: 80,
                    width: "auto",
                    borderRadius: 8,
                    objectFit: "cover",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  }}
                />
                <div style={{ fontSize: 12, color: "#555" }}>
                  {imagenNotificacion?.name} (
                  {Math.round((imagenNotificacion?.size ?? 0) / 1024)} KB)
                  <div style={{ fontSize: 11, color: "#888" }}>
                    Click para ampliar
                  </div>
                </div>
              </div>
            )}
            {previewUrl && (
              <button
                type="button"
                onClick={() => {
                  // 1) Limpia el estado (esto también revoca el ObjectURL vía useEffect)
                  setImagenNotificacion(null);

                  // 2) Limpia la vista previa por si tu efecto no la nulifica inmediatamente
                  setPreviewUrl(null);

                  // 3) Vacía el input para que desaparezca el nombre y permita re-seleccionar el mismo archivo
                  if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                    // opcional: quitar foco
                    fileInputRef.current.blur?.();
                  }
                }}
                style={{
                  marginTop: 8,
                  alignSelf: "flex-start",
                  border: "1px solid #D9D9D9",
                  background: "#fff",
                  padding: "6px 10px",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 12,
                }}
              >
                Quitar imagen
              </button>
            )}
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <CustomButton
          background={"#FFFFFF"}
          color={"#223D51"}
          text="Cancelar"
          height="40px"
          fontWeight="700"
          fontSize="14px"
          border="1px solid #D9D9D9"
          type="button"
          width={"100px"}
          onClick={handleClose}
        />
        <CustomButton
          text={"Enviar"}
          color={"#FFFFFF"}
          background={"#223D51"}
          fontStyle={"normal"}
          fontWeight={500}
          fontSize={"14px"}
          height={"40px"}
          type={"button"}
          onClick={handleSubmit}
        />
      </DialogActions>
    </Dialog>
  );
};

export default NotificationsModal;
